import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowLeft, Smartphone, User, Mail, Phone } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { useDevice } from '../hooks';
import { DeviceStatusBadge } from '../components';

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: device, isLoading, isError, error } = useDevice(id!);

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !device) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Device not found'}</p>
        <Link to="/app/devices">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToList')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/app/devices">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back')}
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Smartphone className="h-6 w-6" />
            <h1 className="text-2xl font-bold">{device.model || `Device #${device.id}`}</h1>
          </div>
          <DeviceStatusBadge status={device.status} />
        </div>
      </div>

      {/* Device Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              {t('deviceInformation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('id')}</p>
                <p className="font-medium">{device.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('model')}</p>
                <p className="font-medium">{device.model || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('version')}</p>
                <p className="font-medium">{device.version || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('locale')}</p>
                <p className="font-medium">{device.locale || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('ipAddress')}</p>
                <p className="font-medium">{device.ip || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('status.status')}</p>
                <DeviceStatusBadge status={device.status} />
              </div>
            </div>
            {device.fcm_token && (
              <div>
                <p className="text-sm text-muted-foreground">{t('fcmToken')}</p>
                <p className="font-medium text-xs break-all">{device.fcm_token}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Information */}
        {device.user && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t('userInformation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">{t('name')}</p>
                <Link
                  to={`/app/users/${device.user.id}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {device.user.name}
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('contact')}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <p>{device.user.phone || '-'}</p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <p>{device.user.email || '-'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('status.status')}</p>
                  <p className="font-medium">{device.user.status || '-'}</p>
                  <p className="text-sm text-muted-foreground mt-2">{t('roleId')}</p>
                  <p>{device.user.roleId || '-'}</p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                  <p className="font-medium">
                    {device.user.createdAt
                      ? format(new Date(device.user.createdAt), 'yyyy-MM-dd HH:mm')
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                  <p className="font-medium">
                    {device.user.updatedAt
                      ? format(new Date(device.user.updatedAt), 'yyyy-MM-dd HH:mm')
                      : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('systemInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
              <p className="font-medium">
                {device.createdAt
                  ? format(new Date(device.createdAt), 'yyyy-MM-dd HH:mm')
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
              <p className="font-medium">
                {device.updatedAt
                  ? format(new Date(device.updatedAt), 'yyyy-MM-dd HH:mm')
                  : '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
